import React from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaTimes, FaArrowRight } from "react-icons/fa";

const ComparisonGrid = ({ dynamicPackages = [] }) => {
  const navigate = useNavigate();

  const smallPkg = dynamicPackages?.find(
    (d) => d.slug?.includes("small") || d.name?.toLowerCase().includes("small")
  );
  const allStarPkg = dynamicPackages?.find(
    (d) =>
      d.slug?.includes("all-star") ||
      d.slug?.includes("allstar") ||
      d.name?.toLowerCase().includes("all-star") ||
      d.name?.toLowerCase().includes("all star")
  );
  const bigLeaguePkg = dynamicPackages?.find(
    (d) => d.slug?.includes("big") || d.name?.toLowerCase().includes("big")
  );

  const hasDynamic = Array.isArray(dynamicPackages) && dynamicPackages.length > 0;
  const isSmallActive = !hasDynamic ? true : Boolean(smallPkg && smallPkg.isActive !== false);
  const isAllStarActive = !hasDynamic ? true : Boolean(allStarPkg && allStarPkg.isActive !== false);
  const isBigLeagueActive = !hasDynamic ? true : Boolean(bigLeaguePkg && bigLeaguePkg.isActive !== false);

  const smallPrice = smallPkg?.price != null ? `$${smallPkg.price}` : "$275";
  const allStarPrice = allStarPkg?.price != null ? `$${allStarPkg.price}` : "$375";
  const bigLeaguePrice = bigLeaguePkg?.price != null ? `$${bigLeaguePkg.price}` : "$475";

  const smallCapacity = smallPkg?.partySize || (smallPkg?.baseParticipants ? `Up to ${smallPkg.baseParticipants} participants` : "Up to 10 participants");
  const allStarCapacity = allStarPkg?.partySize || (allStarPkg?.baseParticipants ? `Up to ${allStarPkg.baseParticipants} participants` : "Up to 15 participants");
  const bigLeagueCapacity = bigLeaguePkg?.partySize || (bigLeaguePkg?.baseParticipants ? `Up to ${bigLeaguePkg.baseParticipants} participants` : "Up to 20 participants");

  const handleSelect = (pkgName) => {
    navigate("/party-request", { state: { selectedPackage: pkgName } });
  };

  const rows = [
    {
      feature: "Package Price",
      small: smallPrice,
      allStar: allStarPrice,
      bigLeague: bigLeaguePrice,
      isHighlight: true,
    },
    {
      feature: "Party Size",
      small: smallCapacity,
      allStar: allStarCapacity,
      bigLeague: bigLeagueCapacity,
      isHighlight: true,
    },
    {
      feature: "Sports Activity Time",
      small: smallPkg?.sportsActivityTime || "90 minutes",
      allStar: allStarPkg?.sportsActivityTime || "90 minutes",
      bigLeague: bigLeaguePkg?.sportsActivityTime || "90 minutes",
    },
    {
      feature: "Sport Choice",
      small: smallPkg?.sportChoice || "⚽ Soccer",
      allStar: allStarPkg?.sportChoice || "Choose 1 Sport",
      bigLeague: bigLeaguePkg?.sportChoice || "Choose 1 Sport",
    },
    {
      feature: "Available Sports",
      small: smallPkg?.availableSports?.join(" • ") || "⚽ Soccer",
      allStar: allStarPkg?.availableSports?.join(" • ") || "⚽ Soccer • 🏀 Basketball • 🏐 Volleyball • 🏏 Cricket",
      bigLeague: bigLeaguePkg?.availableSports?.join(" • ") || "⚽ Soccer • 🏀 Basketball • 🏐 Volleyball • 🏏 Cricket",
    },
    {
      feature: "Playing Area",
      small: smallPkg?.playingArea || "Exclusive use of Small Soccer Field",
      allStar: allStarPkg?.playingArea || "Dedicated field/court appropriate for selected sport",
      bigLeague: bigLeaguePkg?.playingArea || "Dedicated field/court appropriate for selected sport",
    },
    {
      feature: "Party / Social Area",
      small: smallPkg?.partySocialAreaTime || "45–60 minutes",
      allStar: allStarPkg?.partySocialAreaTime || "45–60 minutes",
      bigLeague: bigLeaguePkg?.partySocialAreaTime || "45–60 minutes",
    },
    {
      feature: "Tables & Chairs",
      small: smallPkg?.tablesChairsIncluded !== false ? "Included" : "Not included",
      allStar: allStarPkg?.tablesChairsIncluded !== false ? "Included" : "Not included",
      bigLeague: bigLeaguePkg?.tablesChairsIncluded !== false ? "Included" : "Not included",
      isIncluded: true,
    },
    {
      feature: "Basic Setup & Cleanup",
      small: smallPkg?.setupCleanupIncluded !== false ? "Included" : "Not included",
      allStar: allStarPkg?.setupCleanupIncluded !== false ? "Included" : "Not included",
      bigLeague: bigLeaguePkg?.setupCleanupIncluded !== false ? "Included" : "Not included",
      isIncluded: true,
    },
    {
      feature: "Food & Beverages",
      small: "Available — priced based on actual needs & choices",
      allStar: "Available — priced based on actual needs & choices",
      bigLeague: "Available — priced based on actual needs & choices",
    },
    {
      feature: "Additional Participant",
      small: smallPkg?.extraParticipantPrice ? `$${smallPkg.extraParticipantPrice} / person` : "$20 / person",
      allStar: allStarPkg?.extraParticipantPrice ? `$${allStarPkg.extraParticipantPrice} / person` : "$25 / person",
      bigLeague: bigLeaguePkg?.extraParticipantPrice ? `$${bigLeaguePkg.extraParticipantPrice} / person` : "$30 / person",
      isHighlight: true,
    },
    {
      feature: "Package Capacity",
      small: `Up to ${smallPkg?.maxParticipants || 12} participants with add-ons`,
      allStar: `Up to ${allStarPkg?.maxParticipants || 18} participants with add-ons`,
      bigLeague: "25+ participants: Custom pricing",
      isHighlight: true,
    },
    {
      feature: "Plates / Cups / Napkins / Utensils",
      small: "Not included",
      allStar: "Not included",
      bigLeague: "Not included",
      isExcluded: true,
    },
  ];

  return (
    <div
      id="comparison"
      className="w-full py-16 px-4 md:px-8 lg:px-12 bg-white select-none text-gray-800 border-t border-gray-100"
    >
      <div className="max-w-7xl mx-auto flex flex-col items-center">
        {/* Section Heading */}
        <h2 className="text-3xl sm:text-5xl md:text-6xl font-heading font-black text-primaryBtnColor uppercase text-center tracking-tight leading-tight">
          DETAILED COMPARISON GRID
        </h2>

        {/* Subtitle */}
        <p className="mt-4 text-gray-600 font-subheading text-base sm:text-lg max-w-3xl text-center leading-relaxed">
          One Ultimate Sports Experience. Three Packages to Match Your Group
          Size. Perfect for birthdays, team celebrations, family gatherings,
          social events, and casual get-togethers.
        </p>

        {/* Comparison Table Container */}
        <div className="w-full mt-12 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[840px] text-left border-collapse">
              {/* Header Row with Package Cards */}
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50/70">
                  <th className="p-6 w-1/4 align-top">
                    <span className="font-heading font-extrabold text-xs tracking-wider text-gray-500 uppercase block">
                      FEATURE / INCLUSIONS
                    </span>
                  </th>

                  {/* Small Squad Header */}
                  {isSmallActive && (
                    <th className="p-6 align-top border-l border-gray-200 relative group transition-all duration-200 hover:bg-red-50/20">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-primaryBtnHoverColor transition-colors duration-200"></div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-base">🎈</span>
                          <h3 className="font-heading font-extrabold text-base sm:text-lg text-primaryBtnColor uppercase tracking-wide">
                            {smallPkg?.name ? smallPkg.name.toUpperCase().replace(" PARTY", "") : "SMALL SQUAD"}
                          </h3>
                        </div>
                        <div className="font-heading font-black text-2xl sm:text-3xl text-primaryBtnColor group-hover:text-primaryBtnHoverColor transition-colors mt-1">
                          {smallPrice}
                        </div>
                        <p className="text-xs text-gray-500 font-subheading mt-0.5">
                          {smallCapacity}
                        </p>
                        <button
                          onClick={() => handleSelect("Small Squad")}
                          className="mt-4 w-full bg-white text-primaryBtnColor border border-gray-300 group-hover:bg-primaryBtnHoverColor group-hover:text-white group-hover:border-primaryBtnHoverColor group-hover:shadow-md font-heading font-bold text-xs py-2.5 px-3 rounded-lg uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                        >
                          <span>SELECT</span>
                          <FaArrowRight className="text-[10px]" />
                        </button>
                      </div>
                    </th>
                  )}

                  {/* All-Star Squad Header */}
                  {isAllStarActive && (
                    <th className="p-6 align-top border-l border-gray-200 relative group transition-all duration-200 hover:bg-red-50/20">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-primaryBtnHoverColor transition-colors duration-200"></div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-base">⭐</span>
                          <h3 className="font-heading font-extrabold text-base sm:text-lg text-primaryBtnColor uppercase tracking-wide">
                            {allStarPkg?.name ? allStarPkg.name.toUpperCase().replace(" PARTY", "") : "ALL-STAR SQUAD"}
                          </h3>
                        </div>
                        <div className="font-heading font-black text-2xl sm:text-3xl text-primaryBtnColor group-hover:text-primaryBtnHoverColor transition-colors mt-1">
                          {allStarPrice}
                        </div>
                        <p className="text-xs text-gray-500 font-subheading mt-0.5">
                          {allStarCapacity}
                        </p>
                        <button
                          onClick={() => handleSelect("All-Star Squad")}
                          className="mt-4 w-full bg-white text-primaryBtnColor border border-gray-300 group-hover:bg-primaryBtnHoverColor group-hover:text-white group-hover:border-primaryBtnHoverColor group-hover:shadow-md font-heading font-bold text-xs py-2.5 px-3 rounded-lg uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                        >
                          <span>SELECT</span>
                          <FaArrowRight className="text-[10px]" />
                        </button>
                      </div>
                    </th>
                  )}

                  {/* Big League Header */}
                  {isBigLeagueActive && (
                    <th className="p-6 align-top border-l border-r border-gray-200 relative group transition-all duration-200 hover:bg-red-50/20">
                      <div className="absolute top-0 left-0 right-0 h-1 bg-transparent group-hover:bg-primaryBtnHoverColor transition-colors duration-200"></div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-base">🏆</span>
                          <h3 className="font-heading font-extrabold text-base sm:text-lg text-primaryBtnColor uppercase tracking-wide">
                            {bigLeaguePkg?.name ? bigLeaguePkg.name.toUpperCase().replace(" PARTY", "") : "BIG LEAGUE"}
                          </h3>
                        </div>
                        <div className="font-heading font-black text-2xl sm:text-3xl text-primaryBtnColor group-hover:text-primaryBtnHoverColor transition-colors mt-1">
                          {bigLeaguePrice}
                        </div>
                        <p className="text-xs text-gray-500 font-subheading mt-0.5">
                          {bigLeagueCapacity}
                        </p>
                        <button
                          onClick={() => handleSelect("Big League")}
                          className="mt-4 w-full bg-white text-primaryBtnColor border border-gray-300 group-hover:bg-primaryBtnHoverColor group-hover:text-white group-hover:border-primaryBtnHoverColor group-hover:shadow-md font-heading font-bold text-xs py-2.5 px-3 rounded-lg uppercase tracking-wider transition-all duration-200 flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs active:scale-95"
                        >
                          <span>SELECT</span>
                          <FaArrowRight className="text-[10px]" />
                        </button>
                      </div>
                    </th>
                  )}
                </tr>
              </thead>

              {/* Table Body Rows */}
              <tbody className="divide-y divide-gray-100 font-subheading text-xs sm:text-sm text-gray-700">
                {rows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`transition-colors ${
                      idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"
                    } hover:bg-red-50/15`}
                  >
                    {/* Feature Title */}
                    <td className="p-4 sm:p-5 font-semibold text-gray-900 align-top">
                      {row.feature}
                    </td>

                    {/* Small Squad Column */}
                    {isSmallActive && (
                      <td className="p-4 sm:p-5 border-l border-gray-100 align-top">
                        <div className="flex items-start gap-2">
                          {row.isIncluded ? (
                            <FaCheck className="text-emerald-600 mt-1 flex-shrink-0 text-xs" />
                          ) : row.isExcluded ? (
                            <FaTimes className="text-red-500 mt-1 flex-shrink-0 text-xs" />
                          ) : (
                            <FaCheck className="text-primaryBtnHoverColor mt-1 flex-shrink-0 text-xs" />
                          )}
                          <span
                            className={
                              row.isHighlight
                                ? "font-bold text-primaryBtnColor"
                                : row.isExcluded
                                ? "text-red-600 font-medium"
                                : "text-gray-700 font-medium leading-relaxed"
                            }
                          >
                            {row.small}
                          </span>
                        </div>
                      </td>
                    )}

                    {/* All-Star Squad Column */}
                    {isAllStarActive && (
                      <td className="p-4 sm:p-5 border-l border-gray-100 align-top">
                        <div className="flex items-start gap-2">
                          {row.isIncluded ? (
                            <FaCheck className="text-emerald-600 mt-1 flex-shrink-0 text-xs" />
                          ) : row.isExcluded ? (
                            <FaTimes className="text-red-500 mt-1 flex-shrink-0 text-xs" />
                          ) : (
                            <FaCheck className="text-primaryBtnHoverColor mt-1 flex-shrink-0 text-xs" />
                          )}
                          <span
                            className={
                              row.isHighlight
                                ? "font-bold text-primaryBtnColor"
                                : row.isExcluded
                                ? "text-red-600 font-medium"
                                : "text-gray-700 font-medium leading-relaxed"
                            }
                          >
                            {row.allStar}
                          </span>
                        </div>
                      </td>
                    )}

                    {/* Big League Column */}
                    {isBigLeagueActive && (
                      <td className="p-4 sm:p-5 border-l border-r border-gray-100 align-top">
                        <div className="flex items-start gap-2">
                          {row.isIncluded ? (
                            <FaCheck className="text-emerald-600 mt-1 flex-shrink-0 text-xs" />
                          ) : row.isExcluded ? (
                            <FaTimes className="text-red-500 mt-1 flex-shrink-0 text-xs" />
                          ) : (
                            <FaCheck className="text-primaryBtnHoverColor mt-1 flex-shrink-0 text-xs" />
                          )}
                          <span
                            className={
                              row.isHighlight
                                ? "font-bold text-primaryBtnColor"
                                : row.isExcluded
                                ? "text-red-600 font-medium"
                                : "text-gray-700 font-medium leading-relaxed"
                            }
                          >
                            {row.bigLeague}
                          </span>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonGrid;
